<div class="container-fluid">
  <form [formGroup]="ticketForm">
    @if (showSiteControl) {
      <div class="row mb-3">
        <div class="col-md-12">
          <label for="summary" class="form-label">{{ 'Site' | messageTranslate }}</label>
          <app-site-select id="site_id"
                           bindValue="site_id"
                           formControlName="site_id"/>
          <app-errors [control]="ticketForm.get('site_id')"
                      [translatedErrorMessagesByType]="{required: { messageKey: 'Field is required', parameters: {field: 'Site'} }}"/>
        </div>
      </div>
    }
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="summary" class="form-label">{{ 'Title' | messageTranslate }}</label>
        <input #titleInput
               id="summary"
               type="text"
               class="form-control"
               formControlName="summary"
               [placeholder]="'Enter Title' | messageTranslate"/>
        <app-errors [control]="ticketForm.get('summary')"
                    [translatedErrorMessagesByType]="{required: { messageKey: 'Field is required', parameters: {field: 'Title'} }}"/>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="status" class="form-label">{{ 'Status' | messageTranslate }}</label>
        <app-ace-select id="status"
                        formControlName="status_id"
                        bindValue="status_id"
                        bindLabel="display_text"
                        [clearable]="false"
                        [placeholder]="'Select Status' | messageTranslate"
                        [items]="statusOptions"
                        [multiSelect]="false"/>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="assigned" class="form-label">{{ 'Assigned' | messageTranslate }}</label>
        <app-ace-select id="assigned"
                        formControlName="assignee_id"
                        bindValue="id"
                        bindLabel="fullName"
                        [clearable]="false"
                        [items]="assigneeOptions"
                        [groupBy]="groupByFn"
                        [multiSelect]="false"
                        [loading]="assigneesLoading"
                        [placeholder]="{ messageKey: 'Select Assignee' } | messageTranslate">
          @if (assigneesLoading) {
            <ng-template #labelTemplate let-item="item">
              @switch (item.id) {
                @case (ticket?.assignees?.length && ticket.assignees[0].id) {
                  <div>{{ ticket.assignees[0].fullName }}</div>
                }
                @case (UNASSIGNED_ID) {
                  <div>{{ 'Unassigned' | messageTranslate }}</div>
                }
                @default {
                  <div class="text-body-secondary font-italic mx-1 mb-1">{{ 'Loading...' | messageTranslate }}</div>
                }
              }
            </ng-template>
          }
        </app-ace-select>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="description" class="form-label">{{ 'Description' | messageTranslate }}</label>
        <textarea id="description"
                  class="form-control"
                  rows="3"
                  [placeholder]="'Enter Description' | messageTranslate"
                  formControlName="description"></textarea>
        <app-errors [control]="ticketForm.get('description')"
                    [translatedErrorMessagesByType]="{required: { messageKey: 'Field is required', parameters: {field: 'Description'} }}"/>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="equipment" class="form-label">{{ 'Equipment' | messageTranslate }}</label>
        <app-equipment-select id="equipment"
                              [loading]="equipmentLoading"
                              [bindToEquipmentId]="true"
                              [placeholder]="'Select Equipment' | messageTranslate"
                              [multiSelect]="true"
                              [options]="equipmentOptions"
                              [maxSelectedLabels]="10"
                              [hideValueLabelsWhileLoading]="true"
                              formControlName="equipment_ids"/>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="solution" class="form-label">{{ 'Solution' | messageTranslate }}</label>
        <textarea id="solution" class="form-control"
                  rows="3"
                  [placeholder]="'Enter Solution' | messageTranslate"
                  formControlName="solution"></textarea>
        <app-errors [control]="ticketForm.get('solution')"
                    [translatedErrorMessagesByType]="{required: { messageKey: 'Field is required', parameters: {field: 'Solution'} }}"/>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label for="priority" class="form-label">{{'Priority' | messageTranslate}}</label>
        <app-priority-select id="priority"
                             formControlName="priority_id"
                             [placeholder]="'Select Priority' | messageTranslate"
                             [clearable]="false" />
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-12">
        <label for="impact" class="form-label">{{ 'Impact' | messageTranslate }}</label>
        <app-benefit-select id="impact"
                            formControlName="benefit_id"
                            [clearable]="false"
                            [multiSelect]="false"/>
        <app-errors [control]="ticketForm.get('benefit_ids')"
                    [translatedErrorMessagesByType]="{required: { messageKey: 'Field is required', parameters: {field: 'Impact' | messageTranslate} }}"/>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <label for="savings" class="form-label">
          {{ 'Cost Savings' | messageTranslate }}
          @if (selectedSite; as currentSite) {
            ({{ currentSite.monetary_currency }})
          }
        </label>
        <!-- Not using type="number" as then the browser allows to enter values such as "5+6" and exposes these invalid
             values as null, so they cannot be actually validated using Angular -->
        <input id="savings" class="form-control" type="text" formControlName="savings" [placeholder]="'Enter Cost Savings' | messageTranslate"
               (keypress)="keyPressNumbersWithDecimal($event)"/>
        <app-errors [control]="ticketForm.get('savings')"/>
      </div>
    </div>

    @if (showTimeTaken) {
      <div class="row mt-3">
        <div class="col-md-12">
          <label for="timeTaken" class="form-label">
            {{ 'Time spent' | messageTranslate }}
          </label>
          <app-duration-input formControlName="time_taken" [clearable]="true" />
        </div>
      </div>
    }

    @if (ticket?.ticket_id || isResolvedStatusId(ticketForm.get('status_id').value)) {
      <div class="row mt-3">
        <div class="col-md-12">
          <label for="created_at" class="form-label">{{ 'Created Date' | messageTranslate }}</label>
          <div id="created_at">
            <div class="row align-items-center">
              <!--  only show now/select-timestamp radio buttons when creating new ticket  -->
              @if (!ticket?.ticket_id) {
                <div class="col-md-auto pe-0">
                  <div>
                    <input id="created_at_now" type="radio" formControlName="select_created_at" [value]="false"/>
                    <label for="created_at_now" class="me-2" class="form-label">{{ 'Now' | messageTranslate }}</label>
                  </div>
                  <div>
                    <input id="created_at_custom" type="radio" formControlName="select_created_at" [value]="true"/>
                    <label for="created_at_custom" class="form-label">{{ 'Select Timestamp' | messageTranslate }}</label>
                  </div>
                </div>
              }
              <div class="col-md">
                @if (ticketForm.get('select_created_at').value) {
                  <div>
                    <app-date-time-select formControlName="created_at"
                                          [timezone]="selectedSite?.timezone"
                                          [maxDate]="maxCreatedTs"
                                          [showTime]="true"
                                          [showTimeSpinners]="false"
                                          [translatedErrorMessagesByType]="createdAtTranslatedErrorMessages"/>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <!--  only show resolved_at section when current status_id is set to resolved -->
    @if (isResolvedStatusId(ticketForm.get('status_id').value)) {
      <div class="row mt-3">
        <div class="col-md-12">
          <label for="resolved_at" class="form-label">{{ 'Resolved Date' | messageTranslate }}</label>
          <div id="resolved_at">
            <div class="row align-items-center">
              <!--  only show now/select-timestamp radio buttons when (current status is set to resolved status):
                       1. create a new ticket
                       2. edit existing ticket and set new status to be resolved.
              -->
              @if (!ticket?.ticket_id || !isResolvedStatusId(ticket.status.status_id)) {
                <div class="col-md-auto pe-0">
                  <div>
                    <input id="resolved_at_now" type="radio" formControlName="select_resolved_at" [value]="false"/>
                    <label for="resolved_at_now" class="me-2" class="form-label">{{ 'Now' | messageTranslate }}</label>
                  </div>

                  <div>
                    <input id="resolved_at_custom" type="radio" formControlName="select_resolved_at" [value]="true"/>
                    <label for="resolved_at_custom" class="form-label">{{ 'Select Timestamp' | messageTranslate }}</label>
                  </div>
                </div>
              }
              <div class="col-md">
                @if (ticketForm.get('select_resolved_at').value) {
                  <div>
                    <app-date-time-select formControlName="resolved_at"
                                          [timezone]="selectedSite?.timezone"
                                          [minDate]="minResolvedTs"
                                          [maxDate]="now"
                                          [showTime]="true"
                                          [showTimeSpinners]="false"
                                          [translatedErrorMessagesByType]="resolvedAtTranslatedErrorMessages"/>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <div class="row mt-3">
      <div class="col-md-12">
        <label for="linked_alerts" class="form-label">{{ 'Linked Alerts' | messageTranslate }}</label>
        <div id="linked_alerts">
          <app-ticket-select [ticketType]="TicketType.ALERT"
                             [site]="selectedSite"
                             [inEditMode]="true"
                             [canChangeEditMode]="false"
                             [multiSelect]="true"
                             formControlName="linked_alerts"
                             [canClearSelectionFn]="getCanClearLinkedAlertFn()"
                             [allowToOpenTicketView]="false"
                             [ticketsLoading]="!sitesById"
                             (ticketsLoaded)="onAlertsLoaded($event)"/>
        </div>
      </div>
    </div>
  </form>
</div>
